import { Button, Checkbox, Divider, Flex, HStack, Spinner, Text, VStack } from '@chakra-ui/react';
import { useMerchant } from '@domain/merchants/queries/useMerchant';
import { useTables } from '@domain/merchants/queries/useTables';
import { BASE_URL } from '@olo-web/utils/common/constants';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { useRouter } from 'next/router';
import React, { useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import QRCode from 'react-qr-code';

interface PayAtTableQRCodesProps {
  onClose: () => void;
}

const generateQRCodeImage = async (url: string): Promise<string> => {
  const container = document.createElement('div');
  container.style.cssText =
    'position:fixed;top:-9999px;left:-9999px;width:300px;height:300px;padding:20px;background:white;display:flex;align-items:center;justify-content:center';
  document.body.appendChild(container);

  const root = createRoot(container);
  root.render(React.createElement(QRCode, { value: url, size: 256 }));
  await new Promise((resolve) => setTimeout(resolve, 200));
  const svgElement = container.querySelector('svg');
  if (!svgElement) {
    root.unmount();
    document.body.removeChild(container);
    throw new Error('QR code SVG failed to render');
  }

  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 256;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    root.unmount();
    document.body.removeChild(container);
    throw new Error('Failed to get canvas context');
  }

  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, 256, 256);

  const svgData = new XMLSerializer().serializeToString(svgElement);
  const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
  const svgUrl = URL.createObjectURL(svgBlob);

  const img = new Image();
  await new Promise<void>((resolve, reject) => {
    img.onload = () => {
      ctx.drawImage(img, 0, 0, 256, 256);
      URL.revokeObjectURL(svgUrl);
      resolve();
    };
    img.onerror = reject;
    img.src = svgUrl;
  });

  root.unmount();
  document.body.removeChild(container);

  return canvas.toDataURL('image/png');
};

export const PayAtTableQRCodes = ({ onClose }: PayAtTableQRCodesProps) => {
  const router = useRouter();
  const { data: tables, isLoading } = useTables();
  const { data: merchant } = useMerchant();
  const { locationSlug, merchantSlug } = router.query;
  const [selectedTables, setSelectedTables] = useState<string[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);

  const sortedTables = useMemo(() => {
    if (!tables) return [];
    return [...tables].sort((a, b) => {
      const numA = parseInt(a.number, 10);
      const numB = parseInt(b.number, 10);
      return numA - numB;
    });
  }, [tables]);

  const allTableIds = useMemo(
    () => sortedTables.filter((t) => t.id).map((t) => t.id as string),
    [sortedTables]
  );

  const allSelected = sortedTables.length > 0 && selectedTables.length === allTableIds.length;

  const handleTableChange = (tableId: string) => {
    setSelectedTables((prev) =>
      prev.includes(tableId) ? prev.filter((id) => id !== tableId) : [...prev, tableId]
    );
  };

  const handleSelectAll = () => {
    setSelectedTables(allSelected ? [] : allTableIds);
  };

  const handleDownload = async () => {
    if (selectedTables.length === 0) return;

    setIsDownloading(true);
    try {
      const zip = new JSZip();
      const qrPromises = selectedTables.map(async (tableId) => {
        const table = sortedTables.find((t) => t.id === tableId);
        if (!table?.number) return null;

        const url = `${BASE_URL}/${locationSlug}/${merchantSlug}/${merchant?.merchantId}/pay/table/${tableId}`;
        const dataUrl = await generateQRCodeImage(url);

        return { tableNumber: table.number, dataUrl };
      });

      const qrImages = (await Promise.all(qrPromises)).filter(Boolean) as Array<{
        tableNumber: string;
        dataUrl: string;
      }>;

      qrImages.forEach(({ tableNumber, dataUrl }) => {
        const base64Data = dataUrl.split(',')[1];
        zip.file(`${tableNumber}.png`, base64Data, { base64: true });
      });

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, `${merchantSlug}-pay-qrs.zip`);
    } catch (error) {
      console.error('Error generating QR codes:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  if (isLoading) {
    return (
      <VStack spacing={6} w="100%" justify="center">
        <Spinner color="primary.500" thickness="4px" speed="0.65s" size="xl" />
      </VStack>
    );
  }

  if (!tables || tables.length === 0) {
    return (
      <VStack spacing={6} align="flex-start" w="100%">
        <Text>No tables found.</Text>
      </VStack>
    );
  }

  return (
    <VStack spacing={6} align="flex-start" w="100%">
      <Text>Download and print QR codes for each table in your POS for dine-in payment. </Text>
      <Text pt={3} fontSize="lg" fontWeight="semibold">
        Select Tables
      </Text>
      <VStack spacing={3} align="flex-start" w="100%">
        <Flex w="100%" align="center" justify="space-between">
          <Checkbox size="lg" isChecked={allSelected} onChange={handleSelectAll}>
            <Text fontSize="lg">Select All</Text>
          </Checkbox>
          <Text fontSize="lg" fontWeight="semibold" color="gray.600">
            {selectedTables.length} {selectedTables.length === 1 ? 'Table' : 'Tables'} Selected
          </Text>
        </Flex>
        <Divider />
        <VStack spacing={4} align="flex-start" w="100%" pl={6}>
          {sortedTables.map((table) => {
            if (!table.id || !table.number) return null;
            return (
              <Checkbox
                key={table.id}
                size="lg"
                isChecked={selectedTables.includes(table.id)}
                onChange={() => handleTableChange(table.id)}
              >
                <Text fontSize="lg">Table {table.number}</Text>
              </Checkbox>
            );
          })}
        </VStack>
      </VStack>
      <HStack spacing={4} w="100%" pt={4}>
        <Button variant="outline" size="lg" flex={1} onClick={handleClose}>
          Close
        </Button>
        <Button
          colorScheme="primary"
          size="lg"
          flex={1}
          onClick={handleDownload}
          isLoading={isDownloading}
          isDisabled={selectedTables.length === 0 || isDownloading}
        >
          Download
        </Button>
      </HStack>
    </VStack>
  );
};
